import { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';

/**
 * AdminSettingsPage — `/admin/settings`
 *
 * Global app-wide controls. Source-specific knobs live on their own admin
 * pages:
 *  - Cooldowns + per-source scan limits + jitter → /admin/scan-cadence
 *  - Crawler tuning + discovery / search / dedupe → /admin/source-feeds
 *  - LLM model + prompt → /admin/ai-extraction
 *  - API tokens → /admin/secrets
 *  - Chrome extension keys → /admin/extension-keys
 *  - Discovery radius + neighborhood polygons → /admin/coverage
 */

interface Settings {
  autoPublishConfidence?: number;
  googleCalendarId?: string;
  recurringEventHorizonWeeks?: number;
  recurringMaxOccurrencesPerPost?: number;
  permanentSeriesEnabled?: boolean;
  permanentSeriesHorizonWeeks?: number;
  composeAgentMaxToolCalls?: number;
  publicEventsCacheTtlMs?: number;
  maxUploadBytes?: number;
  // Venue automation
  identifyVenueEnabled?: boolean;
  venueIdentifyMaxPerRun?: number;
  venueIdentifyMinConfidence?: number;
  venueSocialBackfillEnabled?: boolean;
  venueSocialBackfillMaxPerRun?: number;
  venueSocialBackfillMaxAttempts?: number;
  venueSocialBackfillCooldownDays?: number;
  // Venue history classifier
  venueHistoryClassifierEnabled?: boolean;
  venueHistoryClassifierMode?: 'deterministic' | 'hybrid' | 'off';
  venueHistoryAutoDeclineNamePrefixMinDeclines?: number;
  venueHistoryAutoDeclineTypeSimMin?: number;
  venueHistoryAutoDeclineApproveSimMax?: number;
  venueHistoryBoostApproveSimMin?: number;
  venueHistoryBoostDeclineSimMax?: number;
  // Ingest / crawler tuning
  instagramMaxImagesPerPost?: number;
  instagramImagesFetchTimeoutMs?: number;
  websiteMaxPagesPerVenue?: number;
  websiteMaxTotalCrawlMs?: number;
  websiteCrawlDelayMs?: number;
  websiteMaxHtmlBytes?: number;
  websiteFetchTimeoutMs?: number;
  websiteMaxImagesPerPage?: number;
  discoveryMaxResultsPerType?: number;
  customSearchResultsLimit?: number;
  rssItemsSliceLimit?: number;
  dedupeNearDateWindowHours?: number;
}

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    void api.get<Settings>('/api/mgmt/settings').then(setSettings).catch(() => null);
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.patch('/api/mgmt/settings', settings);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <h1>Settings</h1>

      {settings && (
        <section>
          <p className="text-muted">
            Site-wide defaults. Source-specific controls live on their own pages
            (Scan cadence, Source feeds, AI extraction, Secrets, Extension keys, Coverage).
          </p>

          <h2 className="h4 mt-4">Publishing</h2>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="auto-pub" className="form-label">Auto-publish confidence</label>
              <input
                id="auto-pub"
                type="number"
                step={0.05}
                min={0}
                max={1}
                className="form-control"
                value={settings.autoPublishConfidence ?? 0.8}
                onChange={(e) => setSettings({ ...settings, autoPublishConfidence: parseFloat(e.target.value) })}
                aria-describedby="auto-pub-help"
              />
              <div id="auto-pub-help" className="form-text">
                Events with extraction confidence ≥ this auto-publish to Google Calendar (if configured).
              </div>
            </div>
            <div className="col-md-8">
              <label htmlFor="cal-id" className="form-label">Google Calendar ID</label>
              <input
                id="cal-id"
                type="text"
                className="form-control"
                value={settings.googleCalendarId ?? ''}
                onChange={(e) => setSettings({ ...settings, googleCalendarId: e.target.value })}
                aria-describedby="cal-id-help"
              />
              <div id="cal-id-help" className="form-text">
                Target calendar for the publish queue. Leave blank to disable mirroring.
              </div>
            </div>
          </div>

          <h2 className="h4 mt-4">Recurring events</h2>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="rec-horizon" className="form-label">Horizon (weeks)</label>
              <input
                id="rec-horizon"
                type="number"
                min={1}
                max={104}
                className="form-control"
                value={settings.recurringEventHorizonWeeks ?? 12}
                onChange={(e) => setSettings({ ...settings, recurringEventHorizonWeeks: parseInt(e.target.value, 10) })}
                aria-describedby="rec-horizon-help"
              />
              <div id="rec-horizon-help" className="form-text">
                How far into the future to materialize recurring event occurrences.
              </div>
            </div>
            <div className="col-md-4">
              <label htmlFor="rec-max" className="form-label">Max occurrences per post</label>
              <input
                id="rec-max"
                type="number"
                min={1}
                max={500}
                className="form-control"
                value={settings.recurringMaxOccurrencesPerPost ?? 52}
                onChange={(e) => setSettings({ ...settings, recurringMaxOccurrencesPerPost: parseInt(e.target.value, 10) })}
              />
            </div>
          </div>

          <h2 className="h4 mt-4">Permanent series</h2>
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <div className="form-check">
                <input
                  id="perm-enabled"
                  type="checkbox"
                  className="form-check-input"
                  checked={settings.permanentSeriesEnabled ?? false}
                  onChange={(e) => setSettings({ ...settings, permanentSeriesEnabled: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="perm-enabled">Enable permanent series</label>
              </div>
            </div>
            <div className="col-md-4">
              <label htmlFor="perm-horizon" className="form-label">Horizon (weeks)</label>
              <input
                id="perm-horizon"
                type="number"
                min={1}
                max={104}
                className="form-control"
                value={settings.permanentSeriesHorizonWeeks ?? 12}
                onChange={(e) => setSettings({ ...settings, permanentSeriesHorizonWeeks: parseInt(e.target.value, 10) })}
              />
            </div>
          </div>

          <h2 className="h4 mt-4">Performance &amp; limits</h2>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="agent-tools" className="form-label">Compose agent max tool calls</label>
              <input
                id="agent-tools"
                type="number"
                min={1}
                max={50}
                className="form-control"
                value={settings.composeAgentMaxToolCalls ?? 10}
                onChange={(e) => setSettings({ ...settings, composeAgentMaxToolCalls: parseInt(e.target.value, 10) })}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="cache-ttl" className="form-label">Public events cache TTL (ms)</label>
              <input
                id="cache-ttl"
                type="number"
                min={0}
                max={600000}
                step={1000}
                className="form-control"
                value={settings.publicEventsCacheTtlMs ?? 60000}
                onChange={(e) => setSettings({ ...settings, publicEventsCacheTtlMs: parseInt(e.target.value, 10) })}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="max-upload" className="form-label">Max upload size (MB)</label>
              <input
                id="max-upload"
                type="number"
                min={1}
                max={500}
                className="form-control"
                value={Math.round((settings.maxUploadBytes ?? 10485760) / 1048576)}
                onChange={(e) => setSettings({ ...settings, maxUploadBytes: parseInt(e.target.value, 10) * 1048576 })}
              />
            </div>
          </div>

          <button className="btn btn-primary mt-4" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && (
            <span className="ms-3 text-success" role="status">
              ✓ Saved {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}

          <h2 className="h4 mt-5">Venue automation</h2>
          <p className="text-muted small">
            Background jobs that try to attach a venue + social handles to raw posts that came in without one.
          </p>
          <div className="form-check mb-2">
            <input
              id="identify-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.identifyVenueEnabled !== false}
              onChange={(e) => setSettings({ ...settings, identifyVenueEnabled: e.target.checked })}
            />
            <label htmlFor="identify-enabled" className="form-check-label">Identify venue from raw posts</label>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="identify-max" className="form-label">Max identifies per run</label>
              <input id="identify-max" type="number" min={1} max={500} className="form-control"
                value={settings.venueIdentifyMaxPerRun ?? 50}
                onChange={(e) => setSettings({ ...settings, venueIdentifyMaxPerRun: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-4">
              <label htmlFor="identify-conf" className="form-label">Min confidence (0–100)</label>
              <input id="identify-conf" type="number" min={0} max={100} className="form-control"
                value={settings.venueIdentifyMinConfidence ?? 75}
                onChange={(e) => setSettings({ ...settings, venueIdentifyMinConfidence: parseInt(e.target.value, 10) })} />
            </div>
          </div>

          <div className="form-check mt-3 mb-2">
            <input
              id="social-backfill-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.venueSocialBackfillEnabled !== false}
              onChange={(e) => setSettings({ ...settings, venueSocialBackfillEnabled: e.target.checked })}
            />
            <label htmlFor="social-backfill-enabled" className="form-check-label">Backfill missing social handles</label>
          </div>
          <div className="row g-3">
            <div className="col-md-3">
              <label htmlFor="sb-max-run" className="form-label">Max per run</label>
              <input id="sb-max-run" type="number" min={1} max={200} className="form-control"
                value={settings.venueSocialBackfillMaxPerRun ?? 25}
                onChange={(e) => setSettings({ ...settings, venueSocialBackfillMaxPerRun: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="sb-max-att" className="form-label">Max attempts/venue</label>
              <input id="sb-max-att" type="number" min={1} max={10} className="form-control"
                value={settings.venueSocialBackfillMaxAttempts ?? 3}
                onChange={(e) => setSettings({ ...settings, venueSocialBackfillMaxAttempts: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="sb-cooldown" className="form-label">Cooldown (days)</label>
              <input id="sb-cooldown" type="number" min={1} max={90} className="form-control"
                value={settings.venueSocialBackfillCooldownDays ?? 7}
                onChange={(e) => setSettings({ ...settings, venueSocialBackfillCooldownDays: parseInt(e.target.value, 10) })} />
            </div>
          </div>

          <h2 className="h4 mt-5">Venue history classifier</h2>
          <p className="text-muted small">
            Auto-decline / auto-approve hints for candidate venues based on the history of previously declined &amp; approved venues. Tune thresholds carefully — these directly affect what gets surfaced for review.
          </p>
          <div className="form-check mb-2">
            <input id="vh-enabled" type="checkbox" className="form-check-input"
              checked={settings.venueHistoryClassifierEnabled !== false}
              onChange={(e) => setSettings({ ...settings, venueHistoryClassifierEnabled: e.target.checked })} />
            <label htmlFor="vh-enabled" className="form-check-label">Classifier enabled</label>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="vh-mode" className="form-label">Mode</label>
              <select id="vh-mode" className="form-select"
                value={settings.venueHistoryClassifierMode ?? 'hybrid'}
                onChange={(e) => setSettings({ ...settings, venueHistoryClassifierMode: e.target.value as 'deterministic' | 'hybrid' | 'off' })}>
                <option value="off">off</option>
                <option value="deterministic">deterministic</option>
                <option value="hybrid">hybrid</option>
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="vh-prefix" className="form-label">Auto-decline name prefix min declines</label>
              <input id="vh-prefix" type="number" min={1} max={20} className="form-control"
                value={settings.venueHistoryAutoDeclineNamePrefixMinDeclines ?? 3}
                onChange={(e) => setSettings({ ...settings, venueHistoryAutoDeclineNamePrefixMinDeclines: parseInt(e.target.value, 10) })} />
            </div>
          </div>
          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <label htmlFor="vh-ad-type" className="form-label">Auto-decline type-sim min</label>
              <input id="vh-ad-type" type="number" step={0.05} min={0} max={1} className="form-control"
                value={settings.venueHistoryAutoDeclineTypeSimMin ?? 0.7}
                onChange={(e) => setSettings({ ...settings, venueHistoryAutoDeclineTypeSimMin: parseFloat(e.target.value) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="vh-ad-app" className="form-label">Auto-decline approve-sim max</label>
              <input id="vh-ad-app" type="number" step={0.05} min={0} max={1} className="form-control"
                value={settings.venueHistoryAutoDeclineApproveSimMax ?? 0.3}
                onChange={(e) => setSettings({ ...settings, venueHistoryAutoDeclineApproveSimMax: parseFloat(e.target.value) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="vh-bo-app" className="form-label">Boost approve-sim min</label>
              <input id="vh-bo-app" type="number" step={0.05} min={0} max={1} className="form-control"
                value={settings.venueHistoryBoostApproveSimMin ?? 0.5}
                onChange={(e) => setSettings({ ...settings, venueHistoryBoostApproveSimMin: parseFloat(e.target.value) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="vh-bo-dec" className="form-label">Boost decline-sim max</label>
              <input id="vh-bo-dec" type="number" step={0.05} min={0} max={1} className="form-control"
                value={settings.venueHistoryBoostDeclineSimMax ?? 0.3}
                onChange={(e) => setSettings({ ...settings, venueHistoryBoostDeclineSimMax: parseFloat(e.target.value) })} />
            </div>
          </div>

          <h2 className="h4 mt-5">Ingest &amp; crawler tuning</h2>
          <p className="text-muted small">
            Per-source fetch &amp; parse limits. Higher = more coverage; lower = lower cost &amp; less risk of ban / timeout. Defaults are conservative.
          </p>
          <h3 className="h6 mt-3">Instagram</h3>
          <div className="row g-3">
            <div className="col-md-3">
              <label htmlFor="ig-max-imgs" className="form-label">Max images per post</label>
              <input id="ig-max-imgs" type="number" min={1} max={30} className="form-control"
                value={settings.instagramMaxImagesPerPost ?? 10}
                onChange={(e) => setSettings({ ...settings, instagramMaxImagesPerPost: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="ig-fetch-to" className="form-label">Image fetch timeout (ms)</label>
              <input id="ig-fetch-to" type="number" min={3000} max={60000} step={1000} className="form-control"
                value={settings.instagramImagesFetchTimeoutMs ?? 15000}
                onChange={(e) => setSettings({ ...settings, instagramImagesFetchTimeoutMs: parseInt(e.target.value, 10) })} />
            </div>
          </div>
          <h3 className="h6 mt-3">Website crawler</h3>
          <div className="row g-3">
            <div className="col-md-3">
              <label htmlFor="web-max-pages" className="form-label">Max pages / venue</label>
              <input id="web-max-pages" type="number" min={1} max={20} className="form-control"
                value={settings.websiteMaxPagesPerVenue ?? 6}
                onChange={(e) => setSettings({ ...settings, websiteMaxPagesPerVenue: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="web-max-total" className="form-label">Max total crawl (ms)</label>
              <input id="web-max-total" type="number" min={30000} max={180000} step={1000} className="form-control"
                value={settings.websiteMaxTotalCrawlMs ?? 60000}
                onChange={(e) => setSettings({ ...settings, websiteMaxTotalCrawlMs: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="web-crawl-delay" className="form-label">Per-page delay (ms)</label>
              <input id="web-crawl-delay" type="number" min={100} max={10000} step={100} className="form-control"
                value={settings.websiteCrawlDelayMs ?? 1500}
                onChange={(e) => setSettings({ ...settings, websiteCrawlDelayMs: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="web-max-html" className="form-label">Max HTML bytes</label>
              <input id="web-max-html" type="number" min={100000} max={5000000} step={50000} className="form-control"
                value={settings.websiteMaxHtmlBytes ?? 500000}
                onChange={(e) => setSettings({ ...settings, websiteMaxHtmlBytes: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="web-fetch-to" className="form-label">Fetch timeout (ms)</label>
              <input id="web-fetch-to" type="number" min={3000} max={60000} step={1000} className="form-control"
                value={settings.websiteFetchTimeoutMs ?? 15000}
                onChange={(e) => setSettings({ ...settings, websiteFetchTimeoutMs: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="web-max-imgs" className="form-label">Max images / page</label>
              <input id="web-max-imgs" type="number" min={1} max={20} className="form-control"
                value={settings.websiteMaxImagesPerPage ?? 4}
                onChange={(e) => setSettings({ ...settings, websiteMaxImagesPerPage: parseInt(e.target.value, 10) })} />
            </div>
          </div>
          <h3 className="h6 mt-3">Discovery &amp; dedupe</h3>
          <div className="row g-3">
            <div className="col-md-3">
              <label htmlFor="disc-max" className="form-label">Discovery max results / type</label>
              <input id="disc-max" type="number" min={5} max={50} className="form-control"
                value={settings.discoveryMaxResultsPerType ?? 20}
                onChange={(e) => setSettings({ ...settings, discoveryMaxResultsPerType: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="cse-limit" className="form-label">Custom Search results limit</label>
              <input id="cse-limit" type="number" min={1} max={10} className="form-control"
                value={settings.customSearchResultsLimit ?? 3}
                onChange={(e) => setSettings({ ...settings, customSearchResultsLimit: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="rss-slice" className="form-label">RSS items per feed</label>
              <input id="rss-slice" type="number" min={5} max={200} className="form-control"
                value={settings.rssItemsSliceLimit ?? 50}
                onChange={(e) => setSettings({ ...settings, rssItemsSliceLimit: parseInt(e.target.value, 10) })} />
            </div>
            <div className="col-md-3">
              <label htmlFor="dedupe-window" className="form-label">Dedupe near-date window (hours)</label>
              <input id="dedupe-window" type="number" min={1} max={168} className="form-control"
                value={settings.dedupeNearDateWindowHours ?? 36}
                onChange={(e) => setSettings({ ...settings, dedupeNearDateWindowHours: parseInt(e.target.value, 10) })} />
            </div>
          </div>
        </section>
      )}
    </AdminLayout>
  );
}
